import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { FormatIcon, StatusChip } from "@/lib/content";

export default function Library() {
  const [cards, setCards] = useState(null);
  const [muses, setMuses] = useState([]);
  const [formats, setFormats] = useState([]);
  const [f, setF] = useState({ muse_id: "", format_key: "", status: "", q: "" });

  useEffect(() => {
    api.get("/muses").then(({ data }) => setMuses([...data.muses, ...(data.example ? [data.example] : [])]));
    api.get("/formats").then(({ data }) => setFormats(data));
  }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      api.get("/cards", { params }).then(({ data }) => setCards(data));
    }, 250);
    return () => clearTimeout(t);
  }, [f]);

  const fmt = Object.fromEntries(formats.map((x) => [x.key, x]));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div>
      <div className="flex items-end justify-between gap-4 mb-8 fade-up">
        <div><p className="eyebrow mb-3">Content cards</p><h1 className="text-4xl sm:text-5xl lg:text-6xl">Library</h1></div>
        <Link to="/create" className="btn-primary shrink-0" data-testid="library-new-card-button"><Plus size={16} /> New</Link>
      </div>
      <div className="space-y-3 mb-8">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 muted" />
          <input className="field pl-11" value={f.q} onChange={set("q")} placeholder="Search quotes, captions, topics" data-testid="library-search-input" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <select className="field text-xs" value={f.muse_id} onChange={set("muse_id")} data-testid="library-filter-muse">
            <option value="">All muses</option>{muses.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select className="field text-xs" value={f.format_key} onChange={set("format_key")} data-testid="library-filter-format">
            <option value="">All formats</option>{formats.map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}
          </select>
          <select className="field text-xs" value={f.status} onChange={set("status")} data-testid="library-filter-status">
            <option value="">Any status</option><option value="draft">Draft</option><option value="generated">Generated</option><option value="posted">Posted</option>
          </select>
        </div>
      </div>
      {cards === null ? <p className="muted text-sm">Loading…</p> : cards.length === 0 ? (
        <div className="panel p-10 text-center" data-testid="library-empty">
          <p className="font-display text-2xl">Nothing here yet</p>
          <p className="muted text-sm mt-2 mb-6">Your content cards will live here.</p>
          <Link to="/create" className="btn-primary">Create content</Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="library-list">
          {cards.map((c, i) => (
            <Link key={c.id} to={`/library/${c.id}`} className="panel p-4 flex items-center gap-4 hover:border-[#9C4DDC]/50 transition-colors fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`library-card-${c.id}`}>
              <div className="relative shrink-0">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-[#FAF7FE] border border-[#F0EAF7]">
                  {c.muse?.face_reference_url && <img src={imgUrl(c.muse.face_reference_url)} alt="" className="w-full h-full object-cover" />}
                </div>
                <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#FFFFFF] border border-[#9C4DDC]/60 flex items-center justify-center">
                  <FormatIcon icon={fmt[c.format_key]?.icon} size={12} className="text-[#9C4DDC]" />
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm line-clamp-2">“{c.quote}”</p>
                <p className="text-[11px] muted mt-1">{c.muse?.name} · {fmt[c.format_key]?.name} · {new Date(c.created_at).toLocaleDateString()}</p>
              </div>
              <StatusChip status={c.status} testid={`library-card-status-${c.id}`} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
